import { useState, type ChangeEvent } from 'react';
import { useGetList, useNotify, useRefresh } from 'react-admin';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { PermissionGate } from '../PermissionGate';
import { FeatureGate } from '../featureFlags/FeatureGate';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { saveCredential } from './credentialsApi';
import { FEATURE_TRANSPOREON, TRANSPOREON_CREDENTIAL } from '../transporeon/transporeonApi';

/**
 * Konto interfejsu Transporeonu spółki (04.11.2026, decyzja 6: na start
 * tylko spółka polska) — **tylko zapis**, jak SFTP: pola nigdy nie pokazują
 * zapisanej wartości, puste pole = bez zmian. Wartości idą do zaszyfrowanego
 * magazynu per spółka (`transporeon/username`, `company-id`, `password`,
 * opcjonalnie `client-id` — tylko gdy uzgodniony z Transporeonem).
 * Pobieranie i tak rusza wyłącznie na produkcji, z cyklem w konfiguracji
 * wdrożenia. O dostępie rozstrzyga API (zarządzanie kontami).
 */
const FIELDS = [
  { key: TRANSPOREON_CREDENTIAL.USERNAME, label: 'Nazwa użytkownika interfejsu', secret: false },
  { key: TRANSPOREON_CREDENTIAL.COMPANY_ID, label: 'Id spółki w Transporeonie', secret: false },
  { key: TRANSPOREON_CREDENTIAL.PASSWORD, label: 'Hasło', secret: true },
  { key: TRANSPOREON_CREDENTIAL.CLIENT_ID, label: 'Client id (opcjonalnie)', secret: false },
] as const;

const DESCRIPTION: Record<string, string> = {
  [TRANSPOREON_CREDENTIAL.USERNAME]: 'Transporeon — użytkownik interfejsu przewoźnika (tylko pobieranie)',
  [TRANSPOREON_CREDENTIAL.COMPANY_ID]: 'Transporeon — id spółki (login użytkownik@spółka)',
  [TRANSPOREON_CREDENTIAL.PASSWORD]: 'Transporeon — hasło interfejsu przewoźnika',
  [TRANSPOREON_CREDENTIAL.CLIENT_ID]: 'Transporeon — client id uzgodniony z Transporeonem',
};

export const TransporeonCredentialsForm = () => (
  <FeatureGate feature={FEATURE_TRANSPOREON}>
    <PermissionGate permission="MANAGE_USERS">
      <TransporeonCredentialsFields />
    </PermissionGate>
  </FeatureGate>
);

const TransporeonCredentialsFields = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const { data: companies = [] } = useGetList('companies', {
    pagination: { page: 1, perPage: 50 },
    sort: { field: 'id', order: 'ASC' },
  });
  const [companyId, setCompanyId] = useState<number | ''>('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const filled = FIELDS.filter((f) => (values[f.key] ?? '').trim() !== '');

  const save = async () => {
    if (companyId === '') return;
    setBusy(true);
    try {
      for (const f of filled) {
        await saveCredential({
          system: TRANSPOREON_CREDENTIAL.SYSTEM,
          field: f.key,
          value: f.secret ? values[f.key] : values[f.key].trim(),
          companyId,
          description: DESCRIPTION[f.key],
        });
      }
      setValues({});
      notify('Zapisano w magazynie poświadczeń — wartości nie da się tu odczytać', { type: 'success' });
      refresh();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box data-testid="transporeon-credentials" sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Transporeon — konto interfejsu przewoźnika spółki (tylko pobieranie):
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select
          size="small"
          label="Spółka"
          value={companyId}
          sx={{ minWidth: 200 }}
          onChange={(e) => setCompanyId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          {companies.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {String(c.name ?? `spółka nr ${c.id}`)}
            </MenuItem>
          ))}
        </TextField>
        {FIELDS.map((f) => (
          <TextField
            key={f.key}
            size="small"
            label={f.label}
            type={f.secret ? 'password' : 'text'}
            autoComplete={f.secret ? 'new-password' : 'off'}
            value={values[f.key] ?? ''}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
          />
        ))}
        <Button
          size="small"
          variant="outlined"
          disabled={busy || companyId === '' || filled.length === 0}
          onClick={() => void save()}
        >
          Zapisz w magazynie
        </Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Pola nie pokazują zapisanych wartości; puste pole zostawia dotychczasową. Kolejkę czyta wyłącznie produkcja,
        z cyklem ustawionym w konfiguracji wdrożenia.
      </Typography>
    </Box>
  );
};
